import React, { useState, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Input,
  Label,
  Table,
  ButtonGroup,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

function ReporteFactura() {
  const { user } = useContext(AuthContext);
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_ABONOS = process.env.REACT_APP_URL_ABONOS;

  let hoy = new Date();
  let inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  let endDate = moment(hoy).format("YYYY-MM-DD");
  let startDate = moment(inicio).format("YYYY-MM-DD");

  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedEmpresa, setSelectedEmpresa] = useState("");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [notas, setNotas] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  //--- Pagination
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;

  const currency = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n || 0);

  // Los surtidos que todavia no tienen factura asignada traen valores
  // placeholder ("T", "NA", vacio). Esos NO se pueden agrupar entre si:
  // cada surtido va solo, porque son de clientes distintos.
  const PLACEHOLDERS_FACTURA = ["", "T", "NA", "N/A", "-", "."];

  function sinFacturaReal(factura) {
    return PLACEHOLDERS_FACTURA.includes((factura || "").trim().toUpperCase());
  }

  // El numero de nota de credito viene dentro del texto de observaciones,
  // en formatos como "COMP-1537-N/C-909" o "DEV-1889-N/C-898".
  // Si no trae N/C se usa el numero de devolucion que va en formaPago ("Devolucion 1889").
  function numeroNotaCredito(abono) {
    const obs = abono.observaciones || "";
    const nc = obs.match(/N\s*\/?\s*C[-\s]*(\d+)/i);
    if (nc) return nc[1];
    const dev = (abono.formaPago || "").match(/Devolucion\s+(\d+)/i);
    if (dev) return dev[1];
    return "";
  }

  // Carga catálogo clientes una vez
  useMemo(() => {
    axios
      .get(URL_CLIENTES, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((r) => setClientes(r.data))
      .catch(console.error);
  }, []);

  // El reporte se arma desde los ABONOS del periodo:
  // los surtidos que aparecen son los que tuvieron movimiento en esas fechas.
  useMemo(() => {
    showLoader();
    axios
      .get(`${URL_ABONOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((r) => {
        const mapSurtidos = {};

        r.data
          .filter((a) => a.is_active === "Si")
          .forEach((a) => {
            const s = a.surtidos?.[0];
            if (!s) return; // los abonos a notas de cargo no entran aqui

            if (!mapSurtidos[s._id]) {
              mapSurtidos[s._id] = {
                _id: s._id,
                idSurtido: s.idSurtido,
                fechaSurtido: s.fecha,
                factura: (s.factura || "").trim(),
                empresa: s.empresa || "NA",
                total_general: parseFloat(s.total_general) || 0,
                clientes: a.clientes?.[0]?.nombre_comercial || "",
                idClientes: a.clientes?.[0]?._id || "",
                capturas: [], // abonos de pago
                notasCredito: [], // bonificaciones y devoluciones
              };
            }

            const esNota =
              a.concepto === "Bonificacion" || a.concepto === "Devolucion";

            if (esNota) {
              mapSurtidos[s._id].notasCredito.push({
                tipo: a.concepto === "Bonificacion" ? "BONIF" : "DEV",
                numero: numeroNotaCredito(a),
                importe: parseFloat(a.importe) || 0,
                capt: a.idAbono,
              });
            } else {
              mapSurtidos[s._id].capturas.push({
                capt: a.idAbono,
                importe: parseFloat(a.importe) || 0,
                fecha: a.fecha,
              });
            }
          });

        setNotas(Object.values(mapSurtidos));
        hideLoader();
      })
      .catch((e) => {
        console.error(e);
        hideLoader();
      });
  }, [selectedFechaInicio, selectedFechaFin]);

  // Agrupa los surtidos por factura
  const gruposTodos = useMemo(() => {
    const map = {};
    notas.forEach((s) => {
      // Sin factura real cada surtido va en su propio grupo: si se juntaran
      // se mezclarian clientes distintos bajo un mismo renglon.
      // Con factura se agrupa por factura + cliente, porque hay numeros de
      // factura capturados por error en surtidos de clientes distintos.
      const suelto = sinFacturaReal(s.factura);
      const key = suelto ? `SF-${s._id}` : `${s.factura}||${s.idClientes}`;

      if (!map[key]) {
        map[key] = {
          factura: suelto ? "S/F" : s.factura,
          sinFactura: suelto,
          clientes: s.clientes,
          idClientes: s.idClientes,
          empresa: s.empresa,
          surtidos: [],
          importe: 0,
          notas: 0,
          cobrado: 0,
        };
      }
      map[key].surtidos.push(s);
      map[key].importe += s.total_general;
      map[key].notas += s.notasCredito.reduce((acc, n) => acc + n.importe, 0);
      map[key].cobrado += s.capturas.reduce((acc, c) => acc + c.importe, 0);
    });

    Object.values(map).forEach((g) => {
      g.surtidos.sort((a, b) => a.idSurtido - b.idSurtido);
      // El total es lo efectivamente abonado en el periodo, NO el importe
      // facturado: un surtido puede traer abonos parciales.
      g.total = g.cobrado;
    });

    return Object.values(map);
  }, [notas]);

  // Orden por defecto: primero las facturas reales alfabeticamente,
  // al final los surtidos sin factura ordenados por numero de surtido.
  function ordenaGrupos(a, b) {
    if (a.sinFactura !== b.sinFactura) return a.sinFactura ? 1 : -1;
    if (a.sinFactura) {
      return (a.surtidos[0]?.idSurtido || 0) - (b.surtidos[0]?.idSurtido || 0);
    }
    return a.factura < b.factura ? -1 : 1;
  }

  const headers = [
    { name: "Num", field: "factura", sortable: true },
    { name: "Importe", field: "importe", sortable: true },
    { name: "Cliente", field: "clientes", sortable: true },
    { name: "Empresa", field: "empresa", sortable: true },
    { name: "Dev", field: "dev", sortable: false },
    { name: "No-N/C", field: "noNC", sortable: false },
    { name: "Importe N/C", field: "notas", sortable: true },
    { name: "Capt", field: "captNC", sortable: false },
    { name: "Surt", field: "surt", sortable: false },
    { name: "Imp. Surt", field: "impSurt", sortable: false },
    { name: "Capt", field: "capt", sortable: false },
    { name: "Total", field: "total", sortable: true },
  ];

  // Filtrado + orden + paginacion, a nivel FACTURA para no romper los agrupados
  const gruposData = useMemo(() => {
    let computed = gruposTodos;

    if (search) {
      computed = computed.filter((g) => {
        try {
          return (
            g.factura.toLowerCase().includes(search.toLowerCase()) ||
            g.clientes.toLowerCase().includes(search.toLowerCase()) ||
            g.empresa.toLowerCase().includes(search.toLowerCase()) ||
            g.importe.toString().includes(search) ||
            g.total.toString().includes(search) ||
            g.surtidos.some(
              (s) =>
                s.idSurtido.toString().includes(search) ||
                s.capturas.some((c) => c.capt.toString().includes(search)) ||
                s.notasCredito.some((n) => n.capt.toString().includes(search))
            )
          );
        } catch (error) {
          console.error(g, error);
          return false;
        }
      });
    }

    if (selectedCliente) {
      computed = computed.filter((g) => g.idClientes === selectedCliente);
    }

    if (selectedEmpresa) {
      computed = computed.filter((g) => g.empresa === selectedEmpresa);
    }

    setTotalItems(computed.length);

    if (
      sorting.field &&
      sorting.field !== "importe" &&
      sorting.field !== "notas" &&
      sorting.field !== "total"
    ) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = [...computed].sort(
        (a, b) =>
          reversed *
          (a[sorting.field] || "").localeCompare(b[sorting.field] || "")
      );
    }

    if (
      sorting.field &&
      (sorting.field === "importe" ||
        sorting.field === "notas" ||
        sorting.field === "total")
    ) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = [...computed].sort(
        (a, b) =>
          reversed * (parseFloat(a[sorting.field]) - parseFloat(b[sorting.field]))
      );
    }

    if (!sorting.field) {
      computed = [...computed].sort(ordenaGrupos);
    }

    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [
    gruposTodos,
    currentPage,
    search,
    sorting,
    selectedCliente,
    selectedEmpresa,
  ]);

  // Convierte un grupo en filas planas.
  // Por surtido se abren tantos renglones como haga falta para acomodar
  // sus notas de credito y sus abonos de captura.
  function filasDeGrupo(g) {
    const filas = [];
    let primeraDelGrupo = true;

    g.surtidos.forEach((s) => {
      const cuantas = Math.max(
        s.notasCredito.length,
        s.capturas.length,
        1
      );

      for (let i = 0; i < cuantas; i++) {
        const nc = s.notasCredito[i];
        const cap = s.capturas[i];
        filas.push({
          esPrimeraDelGrupo: primeraDelGrupo,
          esPrimeraDelSurtido: i === 0,
          factura: g.factura,
          importeFactura: g.importe,
          totalFactura: g.total,
          dev: nc ? nc.tipo : "",
          numeroNota: nc ? nc.numero : "",
          importeNota: nc ? nc.importe : "",
          captNota: nc ? nc.capt : "",
          // El surtido se repite en cada renglon que trae abono, porque
          // un mismo surtido puede recibir varios abonos en el periodo.
          idSurtido: cap || i === 0 ? s.idSurtido : "",
          fecha: s.fechaSurtido,
          // IMP. SURT es el importe del ABONO, no el total del surtido.
          importeSurtido: cap ? cap.importe : "",
          capt: cap ? cap.capt : "",
        });
        primeraDelGrupo = false;
      }
    });

    return filas;
  }

  // Los mismos grupos que se ven en pantalla, pero SIN paginar (para PDF / Excel)
  const gruposParaExportar = useMemo(() => {
    let computed = gruposTodos;
    if (selectedCliente) {
      computed = computed.filter((g) => g.idClientes === selectedCliente);
    }
    if (selectedEmpresa) {
      computed = computed.filter((g) => g.empresa === selectedEmpresa);
    }
    return [...computed].sort(ordenaGrupos);
  }, [gruposTodos, selectedCliente, selectedEmpresa]);

  // PDF Tabla — replica el formato de la hoja de captura
  function PDFTabla() {
    const doc = new jsPDF("landscape");

    const NEGRO = [0, 0, 0];
    const GRIS = [217, 217, 217];

    const cliente = selectedCliente
      ? clientes.find((c) => c._id === selectedCliente)?.nombre_comercial ||
        "TODOS"
      : "TODOS";

    // --- Encabezado tipo formato: FECHA / CLIENTE con el nombre de la empresa
    doc.autoTable({
      theme: "grid",
      startY: 12,
      margin: { left: 14, right: 14 },
      body: [
        [
          { content: "", styles: { fillColor: GRIS } },
          {
            content: `${process.env.REACT_APP_NOMBRE_INSTITUCION || ""}`,
            styles: { fontStyle: "bold", fontSize: 13, halign: "center" },
          },
        ],
        [
          { content: "FECHA", styles: { fontStyle: "bold", fillColor: GRIS } },
          {
            content: `${selectedFechaInicio}  al  ${selectedFechaFin}`,
            styles: { fontStyle: "bold", fontSize: 11, halign: "center" },
          },
        ],
        [
          {
            content: "CLIENTE",
            styles: { fontStyle: "bold", fillColor: GRIS },
          },
          {
            content: cliente,
            styles: { fontStyle: "bold", fontSize: 11, halign: "center" },
          },
        ],
      ],
      styles: {
        lineColor: NEGRO,
        lineWidth: 0.3,
        textColor: NEGRO,
        cellPadding: 2,
      },
      columnStyles: {
        0: { cellWidth: 38, valign: "middle" },
        1: { cellWidth: 130, valign: "middle" },
      },
    });

    const rows = [];
    let totalImporte = 0;
    let totalNotas = 0;
    let totalNeto = 0;

    gruposParaExportar.forEach((g) => {
      totalImporte += g.importe;
      totalNotas += g.notas;
      totalNeto += g.total;

      filasDeGrupo(g).forEach((f) => {
        rows.push([
          f.esPrimeraDelGrupo ? f.factura : "",
          f.esPrimeraDelGrupo ? currency(f.importeFactura) : "",
          f.dev,
          f.numeroNota,
          f.importeNota === "" ? "" : currency(f.importeNota),
          f.captNota,
          f.idSurtido,
          f.importeSurtido === "" ? "" : currency(f.importeSurtido),
          f.capt,
          f.esPrimeraDelGrupo ? currency(f.totalFactura) : "",
        ]);
      });
    });

    // --- Tabla principal, cuadriculada y con encabezado de dos niveles
    doc.autoTable({
      theme: "grid",
      startY: doc.lastAutoTable.finalY + 4,
      margin: { left: 14, right: 14 },
      head: [
        [
          { content: "FACT", colSpan: 2, styles: { halign: "center" } },
          { content: "DEV", rowSpan: 2 },
          { content: "NO-N/C", rowSpan: 2 },
          { content: "IMPORTE", rowSpan: 2 },
          { content: "CAPT", rowSpan: 2, styles: { fillColor: GRIS } },
          { content: "SURT", rowSpan: 2 },
          { content: "IMP. SURT", rowSpan: 2 },
          { content: "CAPT", rowSpan: 2, styles: { fillColor: GRIS } },
          { content: "TOTAL", rowSpan: 2 },
        ],
        ["NUM", "IMPORTE"],
      ],
      body: rows,
      styles: {
        fontSize: 8,
        cellPadding: 1.8,
        lineColor: NEGRO,
        lineWidth: 0.2,
        textColor: NEGRO,
        valign: "middle",
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: NEGRO,
        fontStyle: "bold",
        fontSize: 8,
        halign: "center",
        valign: "middle",
        lineColor: NEGRO,
        lineWidth: 0.3,
      },
      footStyles: {
        fillColor: GRIS,
        textColor: NEGRO,
        fontStyle: "bold",
        fontSize: 8,
        lineColor: NEGRO,
        lineWidth: 0.3,
      },
      columnStyles: {
        0: { cellWidth: 24, halign: "center" },
        1: { cellWidth: 28, halign: "right" },
        2: { cellWidth: 16, halign: "center" },
        3: { cellWidth: 18, halign: "center" },
        4: { cellWidth: 26, halign: "right" },
        5: { cellWidth: 22, halign: "center", fillColor: GRIS },
        6: { cellWidth: 18, halign: "center" },
        7: { cellWidth: 28, halign: "right" },
        8: { cellWidth: 22, halign: "center", fillColor: GRIS },
        9: { cellWidth: 30, halign: "right" },
      },
      foot: [
        [
          "",
          { content: currency(totalImporte), styles: { halign: "right" } },
          "",
          "",
          { content: currency(totalNotas), styles: { halign: "right" } },
          "",
          "",
          { content: currency(totalNeto), styles: { halign: "right" } },
          { content: "TOTAL", styles: { halign: "right" } },
          { content: currency(totalNeto), styles: { halign: "right" } },
        ],
      ],
      showFoot: "lastPage",
    });

    doc.save(`ReporteFactura-${selectedFechaInicio}-${selectedFechaFin}.pdf`);
  }

  // Excel
  function excel() {
    const rows = [];
    gruposParaExportar.forEach((g) => {
      filasDeGrupo(g).forEach((f) => {
        rows.push({
          Factura: f.esPrimeraDelGrupo ? f.factura : "",
          "Importe Factura": f.esPrimeraDelGrupo ? f.importeFactura : "",
          Cliente: f.esPrimeraDelGrupo ? g.clientes : "",
          Empresa: f.esPrimeraDelGrupo ? g.empresa : "",
          Dev: f.dev,
          "No N/C": f.numeroNota,
          "Importe N/C": f.importeNota,
          "Capt N/C": f.captNota,
          "# Surtido": f.idSurtido,
          Fecha: f.esPrimeraDelSurtido ? f.fecha : "",
          "Imp. Abono": f.importeSurtido,
          Capt: f.capt,
          Total: f.esPrimeraDelGrupo ? f.totalFactura : "",
        });
      });
    });

    const cols = [
      "Factura",
      "Importe Factura",
      "Cliente",
      "Empresa",
      "Dev",
      "No N/C",
      "Importe N/C",
      "Capt N/C",
      "# Surtido",
      "Fecha",
      "Imp. Abono",
      "Capt",
      "Total",
    ];

    var option = {};
    option.fileName = `ReporteFactura-${selectedFechaInicio}-${selectedFechaFin}`;
    option.datas = [
      {
        sheetData: rows,
        sheetName: "ReporteFactura",
        sheetFilter: cols,
        sheetHeader: cols,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  // Totales de lo que se está exportando / viendo filtrado
  const totalImporte = gruposParaExportar.reduce((acc, g) => acc + g.importe, 0);
  const totalNotas = gruposParaExportar.reduce((acc, g) => acc + g.notas, 0);
  const totalNeto = gruposParaExportar.reduce((acc, g) => acc + g.total, 0);

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_admin ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuAdmin"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button
                  size="sm"
                  className="btn"
                  color="danger"
                  onClick={PDFTabla}
                >
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button
                  size="sm"
                  className="btn"
                  color="primary"
                  onClick={excel}
                >
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>

          <h3 align="center">Reporte Por Factura</h3>

          <div className="row">
            <div className="col-md-8">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <Row>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                required
                onChange={(e) => {
                  setSelectedFechaInicio(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Label className="mr-sm-2">Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                required
                onChange={(e) => {
                  setSelectedFechaFin(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
          </Row>
          <br />

          <Table
            size="sm"
            striped
            bordered
            className="table-responsive-xl"
            style={{ borderColor: "#adb5bd" }}
          >
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tr>
              <td></td>
              <td></td>
              <td style={{ paddingTop: "0px" }}>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedCliente}
                  onChange={(e) => {
                    setSelectedCliente(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Selecciona</option>
                  {clientes
                    .sort((a, b) =>
                      a.nombre_comercial > b.nombre_comercial ? 1 : -1
                    )
                    .map((a) => {
                      return (
                        <option value={a._id}>{a.nombre_comercial}</option>
                      );
                    })}
                </Input>
              </td>
              <td style={{ paddingTop: "0px" }}>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedEmpresa}
                  onChange={(e) => {
                    setSelectedEmpresa(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Selecciona</option>
                  {Array.from(new Set(gruposTodos.map((g) => g.empresa)))
                    .sort()
                    .map((e) => (
                      <option value={e}>{e}</option>
                    ))}
                </Input>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tbody>
              {gruposData.length === 0 ? (
                <tr>
                  <td colSpan={12} align="center" className="text-muted">
                    Sin abonos en el periodo
                  </td>
                </tr>
              ) : (
                gruposData.map((g) => {
                  const filas = filasDeGrupo(g);
                  return filas.map((f, i) => (
                    <tr key={`${g.factura}-${f.idSurtido}-${i}`}>
                      {f.esPrimeraDelGrupo ? (
                        <>
                          <td rowSpan={filas.length}>
                            <strong>{f.factura}</strong>
                          </td>
                          <td rowSpan={filas.length}>
                            {currency(f.importeFactura)}
                          </td>
                          <td rowSpan={filas.length}>{g.clientes}</td>
                          <td rowSpan={filas.length}>{g.empresa}</td>
                        </>
                      ) : null}
                      <td>{f.dev}</td>
                      <td>{f.numeroNota}</td>
                      <td>
                        {f.importeNota === "" ? "" : currency(f.importeNota)}
                      </td>
                      <td>{f.captNota}</td>
                      <td>{f.idSurtido}</td>
                      <td>
                        {f.importeSurtido === ""
                          ? ""
                          : currency(f.importeSurtido)}
                      </td>
                      <td>{f.capt}</td>
                      {f.esPrimeraDelGrupo ? (
                        <td rowSpan={filas.length}>
                          <strong>{currency(f.totalFactura)}</strong>
                        </td>
                      ) : null}
                    </tr>
                  ));
                })
              )}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td>
                  <strong>{currency(totalImporte)}</strong>
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <strong>{currency(totalNotas)}</strong>
                </td>
                <td></td>
                <td></td>
                <td>
                  <strong>{currency(totalNeto)}</strong>
                </td>
                <td>
                  <strong>Total</strong>
                </td>
                <td>
                  <strong>{currency(totalNeto)}</strong>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteFactura;
